<p class="font-bold line-height-2" i18n>
  Wireless Service Providers can manage eSIMs over the air (OTA) to update the contents of file system as well as Java applets. These are
  known as Remote File Management (RFM) and Remote Application Management (RAM) respectively. Here, you will set up RFM and RAM instances
  and OTA credentials comprised of keys and connection settings.
</p>

<p class="font-bold line-height-2" i18n>Let's configure the RFM instance first.</p>

<div class="mx-6">
  <rs-error-message *ngIf="errorFound" class="mb-3" [message]="errorMessage" />

  <p-table
    #rfmInstancesTable
    [value]="profileValues.rfmConfiguration"
    [(selection)]="selectedInstances"
    [resizableColumns]="true"
    dataKey="id"
    styleClass="p-datatable-striped p-datatable-gridlines"
    showGridlines="true"
    stripedRows="true"
  >
    <ng-template pTemplate="caption">
      <div class="flex align-items-center gap-2">
        <p-button i18n-label label="Add" icon="pi pi-plus-circle" size="small" [outlined]="true" (onClick)="addInstance()"></p-button>
        <p-button
          i18n-label
          label="Delete"
          icon="pi pi-trash"
          size="small"
          severity="danger"
          [outlined]="true"
          (onClick)="deleteInstance()"
          [disabled]="!selectedInstances || !selectedInstances.length"
        ></p-button>
      </div>
    </ng-template>
    <ng-template pTemplate="colgroup">
      <colgroup>
        <col style="width: 3.5rem" />
        <col style="width: 3rem" />
        <col />
      </colgroup>
    </ng-template>
    <ng-template pTemplate="header">
      <tr>
        <th></th>
        <th><p-tableHeaderCheckbox /></th>
        <th class="text-left" i18n>RFM Instance(s)</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-instance let-expanded="expanded">
      <tr>
        <td>
          <p-button
            [pRowToggler]="instance"
            [text]="true"
            [rounded]="true"
            styleClass="rfm-expanel-toggler"
            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [attr.aria-label]="expanded ? collapseLabel : expandLabel"
          ></p-button>
        </td>
        <td class="text-center">
          <p-tableCheckbox [value]="instance"></p-tableCheckbox>
        </td>
        <td>
          <span class="font-bold mr-2" i18n>Instance AID:</span>
          <div style="display: inline-block; width: 360px">
            <input
              pInputText
              rsHexOnlyInput
              class="maximize-inputs console-text"
              [maxlength]="32"
              [(ngModel)]="instance.instanceAID"
              (input)="handleFormEvents()"
              (focus)="showTip(C.TIP_EWIZ_INSTANCE_AID)"
              (blur)="clearDynamicToast()"
            />
          </div>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="expandedrow" let-instance>
      <tr>
        <td></td>
        <td></td>
        <td>
          <table style="width: auto">
            <tr>
              <th></th>
              <th style="width: 375px"></th>
              <th></th>
            </tr>
            <tr style="vertical-align: top">
              <td>
                <span class="font-bold mr-1" i18n>TAR List</span>
                <span class="mr-2" i18n>(Toolkit Application Reference)</span>
              </td>
              <td colspan="2">
                <p-table
                  [value]="instance.tarList.tars"
                  [(selection)]="instance.tarList.selectedTars"
                  [resizableColumns]="true"
                  dataKey="tarId"
                  styleClass="p-datatable-striped p-datatable-gridlines"
                  showGridlines="true"
                  stripedRows="true"
                >
                  <ng-template pTemplate="caption">
                    <div class="flex align-items-center gap-2">
                      <p-button
                        i18n-label
                        label="Add TAR"
                        icon="pi pi-plus-circle"
                        size="small"
                        [outlined]="true"
                        (onClick)="addTar(instance)"
                      ></p-button>
                      <p-button
                        i18n-label
                        label="Delete TAR(s)"
                        icon="pi pi-trash"
                        size="small"
                        severity="danger"
                        [outlined]="true"
                        (onClick)="deleteTar(instance)"
                        [disabled]="!instance.tarList.selectedTars || !instance.tarList.selectedTars.length"
                      ></p-button>
                    </div>
                  </ng-template>
                  <ng-template pTemplate="colgroup">
                    <colgroup>
                      <col style="width: 3rem" />
                      <col style="width: 4rem" />
                      <col />
                    </colgroup>
                  </ng-template>
                  <ng-template pTemplate="header">
                    <tr>
                      <th>
                        <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                      </th>
                      <th>#</th>
                      <th class="text-left" i18n>TAR Value</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-tar let-rowIndex="rowIndex">
                    <tr>
                      <td class="text-center">
                        <p-tableCheckbox [value]="tar"></p-tableCheckbox>
                      </td>
                      <td class="text-center">{{ rowIndex + 1 }}</td>
                      <td>
                        <input
                          pInputText
                          rsHexOnlyInput
                          class="maximize-inputs console-text"
                          [maxlength]="6"
                          [(ngModel)]="tar.tarValue"
                          (input)="handleFormEvents()"
                        />
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="3" i18n>No TAR Values defined.</td>
                    </tr>
                  </ng-template>
                </p-table>
              </td>
            </tr>
            <tr>
              <td colspan="3"><div class="mb-3"></div></td>
            </tr>
            <tr>
              <td>
                <span class="font-bold mr-1" i18n>MSL</span>
                <span i18n>(Minimum Security Level)</span>
              </td>
              <td>
                <input
                  pInputText
                  rsHexOnlyInput
                  class="maximize-inputs console-text"
                  [maxlength]="2"
                  [(ngModel)]="instance.mslValue"
                  (input)="mslInputEventHandler(instance)"
                />
              </td>
              <td></td>
            </tr>
            <tr>
              <td></td>
              <td colspan="2">
                <div class="mt-2 mb-3" i18n>(e.g., '0E' - Cryptographic Checksum; Ciphering; Counter available)</div>
              </td>
            </tr>
            <tr>
              <td colspan="3">
                <div class="pl-6 pr-6">
                  <div class="mt-2 mb-3 font-italic" i18n>
                    NOTE: Either manually enter MSL value directly above, or use the options below to automatically generate the MSL.
                  </div>
                  <div class="p-3" style="background: var(--rs-gray-100)">
                    <table cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="width: 30%" class="font-bold" i18n>Integrity Check Options</td>
                        <td style="width: 30%" class="font-bold" i18n>Chipering Options</td>
                        <td style="width: 40%" class="font-bold" i18n>Counter Options</td>
                      </tr>
                      <tr>
                        <td colspan="3"><div class="mb-2"></div></td>
                      </tr>
                      <tr style="vertical-align: top">
                        <td>
                          <table>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.integrityCheckOption"
                                  inputId="rfmConfiguration.integrityCheckOption#1"
                                  value="1"
                                  [(ngModel)]="instance.integrityCheckOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.integrityCheckOption#1" i18n>No RC, CC, or DS</label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.integrityCheckOption"
                                  inputId="rfmConfiguration.integrityCheckOption#2"
                                  value="2"
                                  [(ngModel)]="instance.integrityCheckOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.integrityCheckOption#2" i18n>Redundancy Check</label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.integrityCheckOption"
                                  inputId="rfmConfiguration.integrityCheckOption#3"
                                  value="3"
                                  [(ngModel)]="instance.integrityCheckOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.integrityCheckOption#3" i18n>
                                  Cryptographic Checksum
                                </label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.integrityCheckOption"
                                  inputId="rfmConfiguration.integrityCheckOption#4"
                                  value="4"
                                  [(ngModel)]="instance.integrityCheckOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.integrityCheckOption#4" i18n>Digital Signature</label>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td>
                          <table>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.cipheringOption"
                                  inputId="rfmConfiguration.cipheringOption#1"
                                  value="1"
                                  [(ngModel)]="instance.cipheringOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.cipheringOption#1" i18n>No Ciphering</label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.cipheringOption"
                                  inputId="rfmConfiguration.cipheringOption#2"
                                  value="2"
                                  [(ngModel)]="instance.cipheringOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.cipheringOption#2" i18n>Ciphering</label>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td>
                          <table>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.counterOption"
                                  inputId="rfmConfiguration.counterOption#1"
                                  value="1"
                                  [(ngModel)]="instance.counterOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.counterOption#1" i18n>No counter available</label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.counterOption"
                                  inputId="rfmConfiguration.counterOption#2"
                                  value="2"
                                  [(ngModel)]="instance.counterOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.counterOption#2" i18n>
                                  Counter available; no replay or sequence checking
                                </label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.counterOption"
                                  inputId="rfmConfiguration.counterOption#3"
                                  value="3"
                                  [(ngModel)]="instance.counterOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.counterOption#3" i18n>
                                  Process if and only if counter value is higher than the value in the receiving entity
                                </label>
                              </td>
                            </tr>
                            <tr style="vertical-align: top">
                              <td style="width: 2rem">
                                <p-radioButton
                                  (onClick)="mslRadioEventHandler(instance)"
                                  name="rfmConfiguration.counterOption"
                                  inputId="rfmConfiguration.counterOption#4"
                                  value="4"
                                  [(ngModel)]="instance.counterOption"
                                ></p-radioButton>
                              </td>
                              <td>
                                <label class="label-clickable" for="rfmConfiguration.counterOption#4" i18n>
                                  Process if and only if counter value is one higher than the value in the receiving entity
                                </label>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td colspan="3"><div class="mb-3"></div></td>
            </tr>
            <tr>
              <td class="font-bold" i18n>UICC Access Domain</td>
              <td>
                <input
                  pInputText
                  rsHexOnlyInput
                  class="maximize-inputs console-text"
                  [maxlength]="2"
                  [(ngModel)]="instance.uiccAccessDomain"
                  (input)="handleInputRadioToggle(instance, 'uiccAccessDomain')"
                />
              </td>
              <td>
                <table class="ml-3">
                  <tr style="vertical-align: top">
                    <td style="width: 2rem">
                      <p-radioButton
                        (onClick)="instance.uiccAccessDomain = '00'; handleFormEvents()"
                        name="rfmConfiguration.uiccAccessDomainRadioValue"
                        inputId="rfmConfiguration.uiccAccessDomainRadioValue#1"
                        value="1"
                        [(ngModel)]="instance.uiccAccessDomainRadioValue"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="label-clickable" for="rfmConfiguration.uiccAccessDomainRadioValue#1" i18n>
                        '00': Full access to the File System
                      </label>
                    </td>
                  </tr>
                  <tr style="vertical-align: top">
                    <td style="width: 2rem">
                      <p-radioButton
                        (onClick)="instance.uiccAccessDomain = 'FF'; handleFormEvents()"
                        name="rfmConfiguration.uiccAccessDomainRadioValue"
                        inputId="rfmConfiguration.uiccAccessDomainRadioValue#2"
                        value="2"
                        [(ngModel)]="instance.uiccAccessDomainRadioValue"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="label-clickable" for="rfmConfiguration.uiccAccessDomainRadioValue#2" i18n>
                        'FF': No access to the File System
                      </label>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td colspan="3"><div class="mb-3"></div></td>
            </tr>
            <tr>
              <td class="font-bold" i18n>UICC Admin Access Domain</td>
              <td>
                <input
                  pInputText
                  rsHexOnlyInput
                  class="maximize-inputs console-text"
                  [maxlength]="2"
                  [(ngModel)]="instance.uiccAdminAccessDomain"
                  (input)="handleInputRadioToggle(instance, 'uiccAdminAccessDomain')"
                />
              </td>
              <td>
                <table class="ml-3">
                  <tr style="vertical-align: top">
                    <td style="width: 2rem">
                      <p-radioButton
                        (onClick)="instance.uiccAdminAccessDomain = '00'; handleFormEvents()"
                        name="rfmConfiguration.uiccAdminAccessDomainRadioValue"
                        inputId="rfmConfiguration.uiccAdminAccessDomainRadioValue#1"
                        value="1"
                        [(ngModel)]="instance.uiccAdminAccessDomainRadioValue"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="label-clickable" for="rfmConfiguration.uiccAdminAccessDomainRadioValue#1" i18n>
                        '00': Full access to the File System
                      </label>
                    </td>
                  </tr>
                  <tr style="vertical-align: top">
                    <td style="width: 2rem">
                      <p-radioButton
                        (onClick)="instance.uiccAdminAccessDomain = 'FF'; handleFormEvents()"
                        name="rfmConfiguration.uiccAdminAccessDomainRadioValue"
                        inputId="rfmConfiguration.uiccAdminAccessDomainRadioValue#2"
                        value="2"
                        [(ngModel)]="instance.uiccAdminAccessDomainRadioValue"
                      ></p-radioButton>
                    </td>
                    <td>
                      <label class="label-clickable" for="rfmConfiguration.uiccAdminAccessDomainRadioValue#2" i18n>
                        'FF': No access to the File System
                      </label>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td colspan="3"><div class="mb-3"></div></td>
            </tr>
            <tr>
              <td>
                <span class="font-bold mr-2" i18n>ADF AID</span>
                <span i18n>(Optional)</span>
              </td>
              <td>
                <input
                  pInputText
                  rsHexOnlyInput
                  class="maximize-inputs console-text"
                  [maxlength]="32"
                  [(ngModel)]="instance.adfAID"
                  (input)="handleFormEvents()"
                />
              </td>
              <td><span class="ml-3" i18n>(Enter a value between 5 and 16 HEX bytes in length)</span></td>
            </tr>
            <ng-container *ngIf="instance.adfAID?.length > 0">
              <tr>
                <td colspan="3"><div class="mb-3"></div></td>
              </tr>
              <tr>
                <td class="font-bold" i18n>ADF Access Domain</td>
                <td>
                  <input
                    pInputText
                    rsHexOnlyInput
                    class="maximize-inputs console-text"
                    [maxlength]="2"
                    [(ngModel)]="instance.adfAccessDomain"
                    (input)="handleInputRadioToggle(instance, 'adfAccessDomain')"
                  />
                </td>
                <td>
                  <table class="ml-3">
                    <tr style="vertical-align: top">
                      <td style="width: 2rem">
                        <p-radioButton
                          (onClick)="instance.adfAccessDomain = '00'; handleFormEvents()"
                          name="rfmConfiguration.adfAccessDomainRadioValue"
                          inputId="rfmConfiguration.adfAccessDomainRadioValue#1"
                          value="1"
                          [(ngModel)]="instance.adfAccessDomainRadioValue"
                        ></p-radioButton>
                      </td>
                      <td>
                        <label class="label-clickable" for="rfmConfiguration.adfAccessDomainRadioValue#1" i18n>
                          '00': Full access to the File System
                        </label>
                      </td>
                    </tr>
                    <tr style="vertical-align: top">
                      <td style="width: 2rem">
                        <p-radioButton
                          (onClick)="instance.adfAccessDomain = 'FF'; handleFormEvents()"
                          name="rfmConfiguration.adfAccessDomainRadioValue"
                          inputId="rfmConfiguration.adfAccessDomainRadioValue#2"
                          value="2"
                          [(ngModel)]="instance.adfAccessDomainRadioValue"
                        ></p-radioButton>
                      </td>
                      <td>
                        <label class="label-clickable" for="rfmConfiguration.adfAccessDomainRadioValue#2" i18n>
                          'FF': No access to the File System
                        </label>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td colspan="3"><div class="mb-3"></div></td>
              </tr>
              <tr>
                <td class="font-bold" i18n>ADF Admin Access Domain</td>
                <td>
                  <input
                    pInputText
                    rsHexOnlyInput
                    class="maximize-inputs console-text"
                    [maxlength]="2"
                    [(ngModel)]="instance.adfAdminAccessDomain"
                    (input)="handleInputRadioToggle(instance, 'adfAdminAccessDomain')"
                  />
                </td>
                <td>
                  <table class="ml-3">
                    <tr style="vertical-align: top">
                      <td style="width: 2rem">
                        <p-radioButton
                          (onClick)="instance.adfAdminAccessDomain = '00'; handleFormEvents()"
                          name="rfmConfiguration.adfAdminAccessDomainRadioValue"
                          inputId="rfmConfiguration.adfAdminAccessDomainRadioValue#1"
                          value="1"
                          [(ngModel)]="instance.adfAdminAccessDomainRadioValue"
                        ></p-radioButton>
                      </td>
                      <td>
                        <label class="label-clickable" for="rfmConfiguration.adfAdminAccessDomainRadioValue#1" i18n>
                          '00': Full access to the File System
                        </label>
                      </td>
                    </tr>
                    <tr style="vertical-align: top">
                      <td style="width: 2rem">
                        <p-radioButton
                          (onClick)="instance.adfAdminAccessDomain = 'FF'; handleFormEvents()"
                          name="rfmConfiguration.adfAdminAccessDomainRadioValue"
                          inputId="rfmConfiguration.adfAdminAccessDomainRadioValue#2"
                          value="2"
                          [(ngModel)]="instance.adfAdminAccessDomainRadioValue"
                        ></p-radioButton>
                      </td>
                      <td>
                        <label class="label-clickable" for="rfmConfiguration.adfAdminAccessDomainRadioValue#2" i18n>
                          'FF': No access to the File System
                        </label>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </ng-container>
          </table>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="3" i18n>No RFM Instances defined.</td>
      </tr>
    </ng-template>
  </p-table>
</div>
